import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Sparkles, RotateCcw, Clock } from "lucide-react";
import { API_BASE } from "@/services/api";
import SEO from "@/components/SEO";

const DISPLAY = "font-['Outfit']";
const B1 = "#0B1020";

// Self-contained 12-question GS diagnostic (runs fully client-side, no auth).
const QUESTIONS = [
  { q: "Which Article of the Indian Constitution deals with the Right to Equality?", o: { A: "Article 14", B: "Article 19", C: "Article 21", D: "Article 32" }, c: "A", s: "Polity" },
  { q: "The Indus Valley Civilization was mainly spread across which river valley?", o: { A: "Ganga", B: "Yamuna", C: "Indus", D: "Brahmaputra" }, c: "C", s: "History" },
  { q: "Which Schedule of the Constitution contains provisions regarding Panchayati Raj?", o: { A: "9th", B: "10th", C: "11th", D: "12th" }, c: "C", s: "Polity" },
  { q: "The Battle of Plassey was fought in which year?", o: { A: "1757", B: "1764", C: "1857", D: "1947" }, c: "A", s: "History" },
  { q: "Which of the following is NOT a Fundamental Right?", o: { A: "Right to Equality", B: "Right to Property", C: "Right to Freedom", D: "Right against Exploitation" }, c: "B", s: "Polity" },
  { q: "The monsoon season in India generally occurs during:", o: { A: "Mar–May", B: "Jun–Sep", C: "Oct–Nov", D: "Dec–Feb" }, c: "B", s: "Geography" },
  { q: "Which Constitutional Amendment introduced the Anti-Defection Law?", o: { A: "42nd", B: "44th", C: "52nd", D: "73rd" }, c: "C", s: "Polity" },
  { q: "The Quit India Movement was launched in:", o: { A: "1940", B: "1942", C: "1944", D: "1946" }, c: "B", s: "History" },
  { q: "Which soil type is most suitable for cotton cultivation?", o: { A: "Alluvial", B: "Black", C: "Red", D: "Laterite" }, c: "B", s: "Geography" },
  { q: "The Finance Commission is constituted under which Article?", o: { A: "Article 280", B: "Article 300", C: "Article 320", D: "Article 340" }, c: "A", s: "Polity" },
  { q: "The Western Ghats are also known as:", o: { A: "Sahyadris", B: "Nilgiris", C: "Vindhyas", D: "Aravallis" }, c: "A", s: "Geography" },
  { q: "The Dandi March was undertaken against:", o: { A: "Jallianwala Bagh", B: "Salt Tax", C: "Rowlatt Act", D: "Partition" }, c: "B", s: "History" },
];

const getAttribution = () => {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || p.get("source") || null,
    utm_campaign: p.get("utm_campaign") || null,
    campaign_name: p.get("campaign_name") || p.get("campaign") || null,
    keyword: p.get("keyword") || p.get("kw") || null,
  };
};

const Ring = ({ value, label }) => {
  const r = 46, c = 2 * Math.PI * r;
  const tone = value >= 60 ? "#22c55e" : value >= 40 ? "#f97316" : "#ef4444";
  return (
    <div className="relative w-[130px] h-[130px]">
      <svg viewBox="0 0 110 110" className="w-full h-full -rotate-90">
        <circle cx="55" cy="55" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="9" />
        <motion.circle cx="55" cy="55" r={r} fill="none" stroke={tone} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - (c * value) / 100 }}
          transition={{ duration: 1.2, ease: "easeOut" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${DISPLAY} text-4xl font-semibold text-white`}>{value}</span>
        <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
      </div>
    </div>
  );
};

const Bar = ({ label, value }) => {
  const tone = value >= 60 ? "#22c55e" : value >= 40 ? "#f97316" : "#ef4444";
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex justify-between text-xs text-slate-400 mb-1"><span>{label}</span><span>{value}%</span></div>
      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ background: tone }}
          initial={{ width: 0 }} animate={{ width: `${value}%` }} transition={{ duration: 0.9 }} />
      </div>
    </div>
  );
};

export default function FreeDiagnosticPage() {
  const navigate = useNavigate();
  const [attribution] = useState(getAttribution);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const track = useCallback((event, extra = {}) => {
    axios.post(`${API_BASE}/free-diagnostic/event`, { event, ...attribution, ...extra }).catch(() => {});
  }, [attribution]);

  useEffect(() => { track("started"); }, [track]);

  const btnPrimary = "inline-flex items-center justify-center gap-2 rounded-lg bg-[#f97316] hover:bg-[#ea580c] text-white px-6 py-3.5 text-sm font-semibold transition-colors";
  const btnGhost = "inline-flex items-center justify-center gap-2 rounded-lg border border-[rgba(255,255,255,0.15)] hover:border-[rgba(255,255,255,0.3)] text-white px-6 py-3.5 text-sm font-semibold transition-colors";

  const choose = (key) => {
    const next = { ...answers, [idx]: key };
    setAnswers(next);
    if (idx < QUESTIONS.length - 1) setTimeout(() => setIdx(idx + 1), 180);
    else setTimeout(() => finish(next), 200);
  };

  const finish = (final) => {
    let correct = 0, attempted = 0;
    const subj = {};
    QUESTIONS.forEach((qq, i) => {
      subj[qq.s] = subj[qq.s] || { correct: 0, total: 0 };
      subj[qq.s].total++;
      const a = final[i];
      if (a) { attempted++; if (a === qq.c) { correct++; subj[qq.s].correct++; } }
    });
    const score = attempted ? Math.round((correct / attempted) * 100) : 0;
    let weakest = null, wa = 101;
    Object.entries(subj).forEach(([s, v]) => {
      const acc = v.total ? (v.correct / v.total) * 100 : 0;
      if (acc < wa) { wa = acc; weakest = s; }
    });
    let status, verdict, next;
    if (score >= 60) { status = "safe"; verdict = "Your GS foundation is solid."; next = "Focus on revision and reducing careless errors."; }
    else if (score >= 40) { status = "borderline"; verdict = `Borderline — ${weakest} is pulling your score down.`; next = `Strengthen ${weakest} basics before your next test.`; }
    else { status = "unsafe"; verdict = "Your accuracy is below the safe threshold."; next = "Rebuild fundamentals from the static syllabus first."; }
    const subjectScores = Object.entries(subj).map(([s, v]) => ({ subject: s, pct: v.total ? Math.round((v.correct / v.total) * 100) : 0 }));
    const res = { score, correct, attempted, weakest, status, verdict, next, subjectScores };
    setResult(res);
    track("completed", { score, weakest_subject: weakest, status });
  };

  const restart = () => { setAnswers({}); setIdx(0); setResult(null); track("started"); };
  const signup = () => { track("signup_click"); navigate("/auth"); };
  const explore = () => { track("explore_click"); navigate("/upsc"); };

  const progress = Math.round(((result ? QUESTIONS.length : idx) / QUESTIONS.length) * 100);
  const cur = QUESTIONS[idx];

  return (
    <div className={`min-h-screen font-['Inter'] text-white`} style={{ backgroundColor: B1 }} data-testid="free-diagnostic-page">
      <SEO title="Free UPSC Readiness Diagnostic | CLARIAS"
        description="Take a free 5-minute UPSC readiness diagnostic and get your personalized readiness report — score, weakest area and your next step. No signup required to see your result."
        canonical="/free-diagnostic" ogType="website" />

      <div className="max-w-2xl mx-auto px-6 py-10 md:py-16">
        {/* progress */}
        {!result && (
          <div className="mb-8">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
              <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#f97316]" /> Free Readiness Diagnostic</span>
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> ~5 min · Q{idx + 1}/{QUESTIONS.length}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <motion.div className="h-full bg-[#f97316]" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
            </div>
          </div>
        )}

        {/* QUESTION */}
        {!result && (
          <AnimatePresence mode="wait">
            <motion.div key={idx} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
              <p className="text-[11px] uppercase tracking-wide text-[#f97316] mb-3">{cur.s}</p>
              <h2 className={`${DISPLAY} text-xl md:text-2xl font-medium leading-snug mb-7`}>{cur.q}</h2>
              <div className="space-y-3">
                {Object.entries(cur.o).map(([k, v]) => {
                  const active = answers[idx] === k;
                  return (
                    <button key={k} onClick={() => choose(k)} data-testid={`option-${k}`}
                      className={`w-full text-left flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-colors ${active ? "border-[#f97316] bg-[#f97316]/10" : "border-[rgba(255,255,255,0.08)] bg-[#1A2233] hover:border-[rgba(255,255,255,0.2)]"}`}>
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-semibold shrink-0 ${active ? "bg-[#f97316] text-white" : "bg-white/[0.06] text-slate-300"}`}>{k}</span>
                      <span className="text-sm text-slate-200">{v}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex justify-between">
                <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}
                  className={`${btnGhost} disabled:opacity-30`} data-testid="prev-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
                <span className="text-xs text-slate-500 self-center">Pick an option to continue</span>
              </div>
            </motion.div>
          </AnimatePresence>
        )}

        {/* RESULT / REPORT */}
        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="diagnostic-result">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#f97316] mb-2">Your readiness report</p>
            <h1 className={`${DISPLAY} text-3xl md:text-4xl font-semibold tracking-tight mb-8`}>Here's where you stand</h1>

            <div className="rounded-2xl border border-[rgba(255,255,255,0.1)] bg-[#1A2233] p-6 md:p-8">
              <div className="flex flex-col sm:flex-row items-center gap-6 mb-7">
                <Ring value={result.score} label="GS Score" />
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Verdict</p>
                  <p className={`${DISPLAY} text-lg md:text-xl font-medium text-white leading-snug`}>{result.verdict}</p>
                  <p className="text-sm text-slate-400 mt-2">{result.correct}/{result.attempted} correct</p>
                </div>
              </div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-3">Subject-wise accuracy</p>
              {result.subjectScores.map((s) => <Bar key={s.subject} label={s.subject} value={s.pct} />)}
              <div className="mt-6 pt-5 border-t border-[rgba(255,255,255,0.08)] flex items-start gap-2">
                <Check className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white">Your next step</p>
                  <p className="text-sm text-slate-400">{result.next}</p>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-[#f97316]/30 bg-[#f97316]/[0.06] p-6 text-center">
              <p className={`${DISPLAY} text-xl font-medium mb-2`}>Unlock your full preparation plan</p>
              <p className="text-sm text-slate-400 mb-5 max-w-md mx-auto">Save this report, track your readiness over time, and get a personalized study plan built around your weak areas — free to start.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={signup} className={btnPrimary} data-testid="result-signup-btn">Save my report & start free <ArrowRight className="w-4 h-4" /></button>
                <button onClick={explore} className={btnGhost} data-testid="result-explore-btn">Explore CLARIAS</button>
              </div>
            </div>

            <button onClick={restart} className="mt-6 mx-auto flex items-center gap-2 text-sm text-slate-500 hover:text-slate-300" data-testid="restart-btn">
              <RotateCcw className="w-4 h-4" /> Retake diagnostic
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
